import { useContext } from "react";
import { GeneralConfigurationContext } from "features/configuration/GeneralConfiguration/generalConfigurationContexts";
import { cloneDeep, isEqual } from "lodash";
import Switch from "common/Buttons/Switch";
import {
  getCreateItem,
  setItemOverride,
} from "features/configuration/GeneralConfiguration/utils";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import styled from "styled-components";
import {
  getItemOptions,
  getItemOverride,
  getItemValue,
} from "@shared/features/configuration/utils";
import useSharedConfigurationItem from "@shared/features/configuration/useSharedConfigurationItem";

const useConfigurationItem = ({ item, valueParser }) => {
  const { toggleItemModified, idToItem, uuidToOriginalItem, editable } =
    useContext(GeneralConfigurationContext);

  const {
    itemValue,
    checkItemModified,
    handleItemChange,
    itemName,
    itemOptions,
    prefix,
    suffix,
    itemChildren,
    itemShouldDisplay,
  } = useSharedConfigurationItem({
    item,
    valueParser,
    idToItem,
    toggleItemModified,
    uuidToOriginalItem,
  });

  const override = getItemOverride(item);
  const originalItem = uuidToOriginalItem[item.uuid];
  const originalOverride = getItemOverride(originalItem);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);

  const handleOverrideChange = (checked) => {
    setItemOverride(item, checked);
    checkItemModified();
  };

  const checkItemValueModified = () => {
    if (originalOverride === override) {
      return !isEqual(originalItem, item);
    } else {
      const curItem = cloneDeep(item);
      setItemOverride(curItem, originalOverride);
      return !isEqual(curItem, originalItem);
    }
  };

  const checkNewOptionIndex = (value) => {
    if (itemOptions.length) {
      const lastIndex = itemOptions.length - 1;
      if (typeof itemOptions[lastIndex].name === "number") {
        const index = itemOptions.findIndex((option) => option.value > value);
        if (index < 0) return lastIndex;
        return index;
      }
      return 0;
    }
  };

  const addOptionToItem = (value) => {
    const index = checkNewOptionIndex(value);
    itemOptions && itemOptions.splice(index, 0, { name: value, value });
    const originalItemOptions = getItemOptions(originalItem, idToItem);
    originalItemOptions &&
      originalItemOptions.splice(index, 0, { name: value, value });
  };

  const overrideComponent =
    override === null ? null : (
      <StyledSwitch
        checked={override}
        onChange={(checked, e) => {
          handleOverrideChange(checked);
          e.stopPropagation();
        }}
        size={isMobile ? "small" : "default"}
        disabled={!editable}
      />
    );

  return {
    itemValue,
    handleItemChange,
    itemOptions,
    itemName,
    prefix,
    suffix,
    itemChildren,
    itemShouldDisplay,
    overrideComponent,
    checkItemModified,
    createItem: getCreateItem(item, idToItem),
    itemValueModified: checkItemValueModified(),
    originalItemValue: getItemValue(originalItem, idToItem),
    uuid: item.uuid,
    addOptionToItem,
  };
};

export default useConfigurationItem;

const StyledSwitch = styled(Switch)`
  .ant-switch-disabled {
    opacity: 1;
  }
`;
